import React, { MouseEvent as ReactMouseEvent } from "react";
import PropTypes, { ReactNodeLike } from "prop-types";
import { BUTTON_THEME_BLACK } from "./themes";
import styles from "./styles.module.scss";

interface PrimaryButtonProps {
    text?: string;
    children?: ReactNodeLike;
    disabled?: boolean;
    onClick?: ((event: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => void) | undefined;
    autoFocus?: boolean;
    type?: "submit" | "button" | "reset" | undefined;
    theme?: string;
    thinking?: string;
}

interface BasePrimaryButtonProps extends PrimaryButtonProps {
    selectid?: string;
}

const PrimaryButton = React.forwardRef<HTMLButtonElement, PrimaryButtonProps>((props, ref) => {
    if (!props.thinking) {
        return <BasePrimaryButton { ...props } ref={ ref } />;
    }

    return (
        <BasePrimaryButton ref={ ref } { ...props } text={ undefined } disabled={ true }>
            <div className={ styles.thinking }>
                <span className={ styles.dot } />
                <span className={ styles.dot } />
                <span className={ styles.dot } />
            </div>
        </BasePrimaryButton>
    );
});

PrimaryButton.propTypes = {
    text: PropTypes.string,
    children: PropTypes.node,
    disabled: PropTypes.bool,
    onClick: PropTypes.func,
    autoFocus: PropTypes.bool,
    type: PropTypes.oneOf(["submit", "button", "reset"]),
    theme: PropTypes.string,
    thinking: PropTypes.string
};

const BasePrimaryButton = React.forwardRef<HTMLButtonElement, BasePrimaryButtonProps>((props, ref) => {
    const {
        text,
        children,
        disabled,
        onClick,
        autoFocus,
        type,
        theme,
        selectid,
        thinking
    } = props;

    const className = `${ styles.primaryButton } ${ styles[theme as string] }`;

    /* eslint-disable react/button-has-type */
    return (
        <button
            ref={ ref }
            type={ type }
            aria-hidden={ thinking ? "true" : "false" }
            onClick={ onClick }
            className={ className }
            disabled={ disabled }
            autoFocus={ autoFocus }
            { ...{ selectid } }
        >
            <span className={ styles.label }>{ text || children }</span>
        </button>
    );
    /* eslint-enable react/button-has-type */
});

BasePrimaryButton.defaultProps = {
    text: "",
    disabled: false,
    children: undefined,
    onClick: undefined,
    autoFocus: false,
    type: "submit",
    theme: BUTTON_THEME_BLACK
};

BasePrimaryButton.propTypes = {
    text: PropTypes.string,
    disabled: PropTypes.bool,
    onClick: PropTypes.func,
    autoFocus: PropTypes.bool,
    type: PropTypes.oneOf(["submit", "button", "reset"]),
    theme: PropTypes.string,
    selectid: PropTypes.string
};

export default PrimaryButton;
